import React, { useState } from 'react';
import { Card, Table, Button, Space, Tag, Modal, message, Alert } from 'antd';
import { QrcodeOutlined, ReloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import QRCode from 'qrcode.react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import complianceService, { type InvoiceExchangeTransmission } from '@/services/compliance.service';
import AspConnectionPanel from './AspConnectionPanel';

const UAEEInvoicingPage: React.FC = () => {
  const navigate = useNavigate();
  const [qrModalVisible, setQrModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState<InvoiceExchangeTransmission | null>(null);

  const { data: transmissions = [], isLoading, refetch } = useQuery({
    queryKey: ['uae-einvoice-transmissions-all'],
    queryFn: () => complianceService.listTransmissions(),
  });

  const columns: ColumnsType<InvoiceExchangeTransmission> = [
    {
      title: 'Document',
      key: 'document',
      render: (_, row) => {
        if (row.document_type === 'SalesInvoice') {
          return (
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => navigate(`/growth/invoices/${row.document_id}`)}
            >
              Tax invoice
            </Button>
          );
        }
        if (row.document_type === 'CustomerCreditNote') {
          return (
            <Button
              type="link"
              style={{ padding: 0 }}
              onClick={() => navigate(`/fin/credit-notes/${row.document_id}`)}
            >
              Credit note
            </Button>
          );
        }
        return (
          <span>
            {row.document_type} · {row.document_id.slice(0, 8)}…
          </span>
        );
      },
    },
    {
      title: 'Dir',
      dataIndex: 'direction',
      key: 'direction',
      width: 90,
      render: (d: string) => d || 'OUTBOUND',
    },
    {
      title: 'Adapter',
      dataIndex: 'adapter_code',
      key: 'adapter_code',
      width: 100,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (s: string) => (
        <Tag
          color={
            s === 'Cleared'
              ? 'green'
              : s === 'Rejected' || s === 'Failed'
                ? 'red'
                : s === 'Transmitted'
                  ? 'blue'
                  : 'default'
          }
        >
          {s}
        </Tag>
      ),
    },
    {
      title: 'ASP status',
      key: 'provider',
      render: (_, row) =>
        row.provider_status || row.provider_tax_status ? (
          <span style={{ fontSize: 12 }}>
            {row.provider_status || '—'}
            {row.provider_tax_status ? ` / ${row.provider_tax_status}` : ''}
          </span>
        ) : (
          '—'
        ),
    },
    {
      title: 'Entry',
      dataIndex: 'provider_entry_id',
      key: 'provider_entry_id',
      width: 100,
      ellipsis: true,
    },
    {
      title: 'UUID',
      dataIndex: 'exchange_uuid',
      key: 'exchange_uuid',
      ellipsis: true,
      width: 160,
    },
    {
      title: 'Requested',
      dataIndex: 'request_at',
      key: 'request_at',
      render: (d: string) => (d ? dayjs(d).format('DD-MMM-YYYY HH:mm') : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, row) => (
        <Space>
          {row.qr_payload ? (
            <Button
              type="link"
              icon={<QrcodeOutlined />}
              onClick={() => {
                setSelectedRow(row);
                setQrModalVisible(true);
              }}
            >
              QR
            </Button>
          ) : null}
          {row.provider_entry_id ? (
            <Button
              type="link"
              onClick={async () => {
                try {
                  await complianceService.refreshAspEntryStatus(row.provider_entry_id!);
                  message.success('Status refreshed');
                  refetch();
                } catch (err: any) {
                  message.error(err?.response?.data?.message || err?.message || 'Refresh failed');
                }
              }}
            >
              Refresh ASP
            </Button>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '24px' }}>
      <Alert
        message="ORDAN UAE e-invoicing"
        description="Generate from posted Tax Invoices, Credit Notes, Direct Purchase Invoices, or Supplier Bills. With Aigentrix enabled, submissions go through the accredited ASP (Peppol + FTA reporting). Local TLV QR remains available for print."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <AspConnectionPanel />

      <Card
        title="Transmission register"
        extra={
          <Space>
            <Button onClick={() => navigate('/compliance/inbound-einvoices')}>
              Inbound workbench
            </Button>
            <Button icon={<ReloadOutlined />} onClick={() => refetch()}>
              Refresh
            </Button>
          </Space>
        }
      >
        <Table
          columns={columns}
          dataSource={transmissions}
          rowKey="id"
          loading={isLoading}
          scroll={{ x: 1100 }}
          pagination={{
            defaultPageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `${total} transmissions`,
          }}
          locale={{ emptyText: 'No UAE e-invoice transmissions yet.' }}
        />
      </Card>

      <Modal
        title="UAE E-Invoice QR"
        open={qrModalVisible}
        onCancel={() => setQrModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setQrModalVisible(false)}>
            Close
          </Button>,
        ]}
      >
        {selectedRow?.qr_image_data_url ? (
          <img src={selectedRow.qr_image_data_url} alt="QR" style={{ maxWidth: '100%' }} />
        ) : selectedRow?.qr_payload ? (
          <div style={{ textAlign: 'center' }}>
            <QRCode value={selectedRow.qr_payload} size={220} />
          </div>
        ) : null}
      </Modal>
    </div>
  );
};

export default UAEEInvoicingPage;
